import React, { useCallback, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { SwButtonSection, getFetcher } from '../../../App';
import { Input, PrimaryButton, Sub1Button, SelectSearchDicomTag, Switch } from '@components/atoms';
import { useSetRecoilState } from 'recoil';
import { dialogContentsState } from '@components/templates/Dialog';
import SettingCommonMethods from '../../../utils/settingCommon';
import { useCatchLeave } from '../../../utils/hooks';
import { StyledReportWrap, StyledReportArea, StyledReportFlex } from './style';
import {
  StyledcloseButton,
  StyledConfirmButton,
  StyledMetatag,
  StyledMetatagAddButton,
  StyledMetatagBox,
  StyledMetatagItem,
  StyledMetatagItemWrap,
  StyledTxtSuccess,
  StyledValue,
} from '@pages/SW/Metatag/style';
import he from 'he';
import { submitDebounce } from '../../../utils/lib';

interface DicomInfo {
  definition: string;
  dicomTagId?: number;
  isNumericVR?: boolean;
  isSequential?: boolean;
  tag: string;
  value?: string;
  vr: string;
  valid?: boolean;
  isChanged?: boolean;
}

const ReportSettingPage = () => {
  const [switchIndex, setSwitchIndex] = useState(0);
  const [reportData, setReportData] = useState(false);
  const [defaultReportData, setDefaultReportData] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [dicomTagList, setDicomTagList] = useState<DicomInfo[]>([]);
  const [metaList, setMetaList] = useState<DicomInfo[]>([]);
  const [originMetaList, setOriginMetaList] = useState<DicomInfo[]>([]);
  const setDialogContents = useSetRecoilState(dialogContentsState);
  const {
    makeMetaList,
    delMetaList,
    metaListStatus,
    metaListLocalValid,
    metaListChangeData,
    getDicomTagAll,
    errorHandler,
    checkValidAllClear,
  } = SettingCommonMethods();

  // 값 변경이 있는 경우 새로고침, 탭닫기 막기
  useCatchLeave(isChanged);

  /**
   * dicom tag 가져오기
   */
  const getDicomTag = async () => {
    const _result = await getDicomTagAll();
    setDicomTagList(_result.result);
    await getReportSetting();
  };

  /**
   * report 설정 값 가져오기
   */
  const getReportSetting = async () => {
    const url = `api/sw/custom/tag`;
    const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;

    await getFetcher({
      method: 'GET',
      accessToken: token,
    })(url)
      .then((r) => {
        if (r.code === '200') {
          if (r.result.tagCustomizationInfos.length > 0) {
            r.result.tagCustomizationInfos.forEach((data: DicomInfo) => {
              data.isChanged = false;
              data.valid = true;
            });
            setMetaList(r.result.tagCustomizationInfos);
            setOriginMetaList(r.result.tagCustomizationInfos);
          }
          setReportData(r.result.isRelayPacsReport);
          setDefaultReportData(r.result.isRelayPacsReport);
          setSwitchIndex(switchIndex + 1);
        } else {
          errorHandler(r);
        }
      })
      .catch((e) => {
        console.log(e, 'e');
      });
  };

  /**
   * metaList 유효성 체크
   */
  const getCheckMetaTagValid = async (idx: number) => {
    if (!metaList[idx].tag || !metaList[idx].definition) return false;

    // 로컬 유효성 체크
    const _localValid = metaListLocalValid(idx, metaList);

    // 통과시 서버 유효성 체크
    if (_localValid) {
      // valid 체크
      const url = `api/sw/custom/tag/validate`;
      const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;
      const _params = {
        dicomTagId: metaList[idx].dicomTagId,
        value: metaList[idx].value,
      };

      await getFetcher({
        method: 'POST',
        accessToken: token,
        body: _params,
      })(url)
        .then(async (r) => {
          await metaListChange(r.code, idx, 'VALID');
        })
        .catch((e) => {
          console.log(e, 'e');
        });
    }
  };

  /**
   * 변경값 저장 하기
   */
  const postReportSetting = async () => {
    // valid 체크
    const url = `api/sw/custom/tag`;
    const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;
    const _tmpBody: any = {
      isRelayPacsReport: reportData,
      tagCustomizationInfos: originMetaList,
    };
    const _tagList: any = [];

    if (reportData) {
      metaList.forEach((data) => {
        const _tmp = {
          dicomTagId: data.dicomTagId,
          isSequential: data.isSequential,
          value: data.value,
        };
        _tagList.push(_tmp);
      });

      _tmpBody.tagCustomizationInfos = _tagList;
    }

    await getFetcher({
      method: 'POST',
      accessToken: token,
      body: _tmpBody,
    })(url)
      .then(async (r) => {
        if (r.code === '200') {
          setDialogContents({
            type: 'plain',
            body: `It has been reflected.`,
            footer: (
              <PrimaryButton
                onClick={async () => {
                  setDialogContents({ type: 'hidden' });
                }}
              >
                OK
              </PrimaryButton>
            ),
          });
          setDefaultReportData(reportData);
          setOriginMetaList(metaList);
        } else {
          errorHandler(r);
        }
      })
      .catch((e) => {
        console.log(e, 'e');
      });
  };

  /**
   * report 사용여부 변경 전 체크
   */
  const isChangeSetting = async (e: boolean) => {
    if (!e && defaultReportData) {
      setDialogContents({
        type: 'plain',
        body: `Are you sure you want to turn off automatically report send? \n\r(You must click Submit for the changes to take effect.)`,
        footer: (
          <>
            <Sub1Button
              onClick={() => {
                setSwitchIndex(switchIndex + 1);
                setDialogContents({ type: 'hidden' });
              }}
            >
              {`No, I don't`}
            </Sub1Button>
            <PrimaryButton
              onClick={async () => {
                await setReportData(e);
                setMetaList(originMetaList);
                setDialogContents({ type: 'hidden' });
              }}
            >
              Yes, turn off
            </PrimaryButton>
          </>
        ),
      });
      return;
    }
    await setReportData(e);
  };

  /**
   * metaList 변경 또는 유효성 확인
   * @param e
   * @param index
   * @param type
   */
  const metaListChange = useCallback(
    (e: any, index: number, type: string) => {
      metaListChangeData(e, index, type, metaList, originMetaList, setMetaList);
    },
    [metaList, originMetaList],
  );

  /**
   * metalist 유효성 모두 통과 여부 확인
   */
  useEffect(() => {
    if (!reportData) {
      // send가 false 이면 기본 데이터 비교 해서 활성화 여부
      setIsChanged(reportData !== defaultReportData);
    } else {
      // send가 true 이면 현재 변경 데이터 확인
      const _changeReportSend = reportData !== defaultReportData;
      const _changeMetaList = checkValidAllClear(metaList, originMetaList);

      setIsChanged(_changeReportSend || _changeMetaList);
    }
  }, [reportData, defaultReportData, metaList, originMetaList]);

  /**
   * 최초 데이터 설정
   */
  useEffect(() => {
    (async () => {
      await getDicomTag();
    })();
  }, []);

  return (
    <>
      <label>
        Report <span className={'sub-title'}>Set up reports that are sent to PACS.</span>
      </label>
      <StyledReportWrap>
        <StyledReportArea>
          <strong>Send</strong>
          <StyledReportFlex>
            <span className={'send-title'}>Send report automatically.</span>
            <span className={'send-off'}>Off</span>
            <Switch key={switchIndex} defaultValue={reportData} onChangeValue={(e) => isChangeSetting(e)} />
            <span>On</span>
          </StyledReportFlex>
        </StyledReportArea>
      </StyledReportWrap>
      {reportData && (
        <StyledMetatagItemWrap>
          <StyledMetatagBox>
            {/*
            StyledMetatagItem상태에 따라 클래스 추가/제거
            disable, active, error, success
          */}
            {metaList &&
              metaList.map((item, index) => {
                return (
                  <StyledMetatagItem key={index} className={`report ${metaListStatus(item)}`}>
                    <StyledMetatag>
                      <div className="title">Metatag</div>
                      {/*검색*/}
                      <SelectSearchDicomTag
                        dicomTagList={dicomTagList}
                        value={item}
                        dataIdx={index}
                        onChangeFunc={metaListChange}
                      />
                    </StyledMetatag>
                    <StyledValue className="value">
                      <div className="title">Value</div>
                      <Input
                        type={`text`}
                        name={`tel5`}
                        placeholder={`Value`}
                        className={item.isNumericVR ? 'sequence-type' : ''}
                        value={item.value ? he.decode(item.value) : ''}
                        onChange={(e) => {
                          metaListChange(e, index, 'VALUE');
                        }}
                      />
                      {item.value && !item.valid && <p className="error-msg">Invalid value</p>}
                      {item.isNumericVR && (
                        <Input
                          className={'check-input'}
                          type={'checkbox'}
                          name={'Sequence'}
                          checked={item.isSequential}
                          onChange={(e) => {
                            metaListChange(e, index, 'SEQUENCE');
                          }}
                        >
                          {'Sequence'}
                        </Input>
                      )}
                    </StyledValue>

                    {item.tag && item.value && !item.isChanged && item.valid ? (
                      <StyledTxtSuccess>
                        <span>Success!</span>
                      </StyledTxtSuccess>
                    ) : (
                      <StyledConfirmButton
                        className="bnt-confirm"
                        onClick={submitDebounce(async () => {
                          await getCheckMetaTagValid(index);
                        })}
                      >
                        Confirm
                      </StyledConfirmButton>
                    )}
                    <StyledcloseButton onClick={() => delMetaList(index, metaList, setMetaList)} />
                  </StyledMetatagItem>
                );
              })}
          </StyledMetatagBox>
          <StyledMetatagAddButton onClick={() => makeMetaList(metaList, setMetaList)}>
            <span>Add metatag</span>
          </StyledMetatagAddButton>
        </StyledMetatagItemWrap>
      )}

      <SwButtonSection>
        <PrimaryButton
          styles={css`
            width: 145px;
            height: 48px;
            margin-top: 8px;
            font-size: 18px;
          `}
          onClick={submitDebounce(postReportSetting)}
          disabled={!isChanged}
        >
          Submit
        </PrimaryButton>
      </SwButtonSection>
    </>
  );
};

export default ReportSettingPage;
